/**
 * Étape Élevage — questions détaillées + travaux ciblés.
 *
 * Adapté au type d'élevage déclaré : si bovins lait → questions tank et
 * salle de traite + travaux récup-tank / pré-refroidisseur. Si volailles
 * → échangeur air/air, etc.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import {
  ArrowRight,
  Beef,
  CheckCircle2,
  Info,
  Milk,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import {
  ELEVAGE_TYPES,
  TRAVAUX_AGRI,
  type ElevageType,
  type TravauxAgriOption,
} from './agri-config';
import { useAgriStore } from './useAgriStore';

export function Step3bElevage() {
  const elevage = useAgriStore((s) => s.elevage);
  const travaux = useAgriStore((s) => s.travaux);
  const patchElevage = useAgriStore((s) => s.patchElevage);
  const toggleTravail = useAgriStore((s) => s.toggleTravail);
  const goNext = useAgriStore((s) => s.goNext);

  const hasLaitier = elevage.types.includes('bovin-lait');
  const hasVolaille = elevage.types.includes('volaille');
  const hasPorcin = elevage.types.includes('porcin');

  /* — Travaux élevage à afficher — */
  const elevageItems = useMemo(
    () => TRAVAUX_AGRI.filter((t) => t.category === 'elevage'),
    [],
  );

  /* — Recommandations basées sur le contexte — */
  const recommendedIds = useMemo(() => {
    const r: string[] = [];
    if (hasLaitier) {
      if (elevage.hasTank) {
        r.push('elev-recup-tank', 'elev-prerefroidisseur');
      }
      r.push('elev-pac');
    }
    if (hasVolaille) {
      r.push('elev-echangeur-volaille');
    }
    if (hasPorcin) {
      r.push('elev-pac');
    }
    return Array.from(new Set(r));
  }, [hasLaitier, hasVolaille, hasPorcin, elevage.hasTank]);

  const minimumAnswered = elevage.types.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <Beef className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-700">
          Section élevage · questions détaillées
        </p>
      </div>
      <h2 className="mt-2 h-section">
        Parlons de votre élevage
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Vos réponses calibrent les <strong>travaux CEE thermique</strong> les
        plus rentables : pompe à chaleur, récupération sur tank à lait,
        échangeur d'air, pré-refroidisseur…
      </p>

      {/* ── Types d'animaux ─────────────────────────────────── */}
      <SubField label="Types d'animaux (sélection multiple)">
        <ChipGridMulti<ElevageType>
          options={ELEVAGE_TYPES}
          values={elevage.types}
          onChange={(v) => patchElevage({ types: v })}
        />
      </SubField>

      {/* ── Effectif ────────────────────────────────────────── */}
      <SubField label="Effectif approximatif (têtes)">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="Ex : 80"
          value={
            elevage.effectif != null ? elevage.effectif.toLocaleString('fr-FR') : ''
          }
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '');
            patchElevage({ effectif: raw === '' ? null : parseInt(raw, 10) });
          }}
          className="h-12 rounded-xl border-2 border-stone-200 text-[15px] font-medium tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
        />
      </SubField>

      {/* ── Questions laitier ───────────────────────────────── */}
      <AnimatePresence>
        {hasLaitier && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="mt-5 rounded-2xl border border-brand-200/70 bg-brand-50/30 p-4">
              <div className="flex items-center gap-2">
                <Milk
                  className="h-3.5 w-3.5 text-brand-700"
                  strokeWidth={2}
                />
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-700">
                  Spécifique élevage laitier
                </p>
              </div>
              <p className="mt-1 text-[12px] leading-relaxed text-brand-900/80">
                Plusieurs fiches CEE sont dédiées aux exploitations laitières
                (récupération de chaleur, pré-refroidisseur, PAC).
              </p>

              <div className="mt-3 space-y-3">
                <SubField label="Tank à lait sur l'exploitation ?">
                  <div className="flex gap-2">
                    <BigYesNo
                      label="Oui"
                      selected={elevage.hasTank === true}
                      onClick={() => patchElevage({ hasTank: true })}
                    />
                    <BigYesNo
                      label="Non"
                      selected={elevage.hasTank === false}
                      onClick={() => patchElevage({ hasTank: false })}
                    />
                  </div>
                </SubField>
                <SubField label="Salle de traite ?">
                  <div className="flex gap-2">
                    <BigYesNo
                      label="Oui"
                      selected={elevage.hasSalleTraite === true}
                      onClick={() => patchElevage({ hasSalleTraite: true })}
                    />
                    <BigYesNo
                      label="Non"
                      selected={elevage.hasSalleTraite === false}
                      onClick={() => patchElevage({ hasSalleTraite: false })}
                    />
                  </div>
                </SubField>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Travaux élevage suggérés ──────────────────────────── */}
      <div className="mt-7">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-accent-700" strokeWidth={2.2} />
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent-900">
            Travaux CEE élevage applicables
          </p>
        </div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-stone-500">
          Sélectionnez ce qui vous intéresse — les recommandations s'adaptent
          au type d'élevage déclaré.
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {elevageItems.map((item) => (
            <TravailCard
              key={item.id}
              item={item}
              selected={travaux.includes(item.id)}
              recommended={recommendedIds.includes(item.id)}
              onToggle={toggleTravail}
            />
          ))}
        </div>
      </div>

      <Button
        variant="brand"
        size="xl"
        disabled={!minimumAnswered}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Info className="h-3 w-3" strokeWidth={1.8} />
        Vous pourrez ajouter d'autres travaux à l'étape "Récapitulatif"
      </p>
    </div>
  );
}

/* ─── Helpers UI (locaux pour éviter le couplage) ────────── */

function SubField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5">
      <Label className="text-[12.5px] font-semibold text-ink">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function BigYesNo({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border-2 text-[13.5px] font-semibold transition-all duration-150',
        selected
          ? 'border-brand-500 bg-brand-50 text-brand-800'
          : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300',
      )}
    >
      {selected && <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} />}
      {label}
    </button>
  );
}

interface ChipOption<T extends string> {
  id: T;
  label: string;
  sublabel?: string;
  icon?: LucideIcon;
}

function ChipGridMulti<T extends string>({
  options,
  values,
  onChange,
}: {
  options: ChipOption<T>[];
  values: T[];
  onChange: (v: T[]) => void;
}) {
  function toggle(id: T) {
    if (values.includes(id)) onChange(values.filter((v) => v !== id));
    else onChange([...values, id]);
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = values.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => toggle(opt.id)}
            className={cn(
              'flex items-start gap-2.5 rounded-xl border-2 p-3 text-left transition-all duration-150',
              selected
                ? 'border-brand-500 bg-brand-50 shadow-[0_2px_8px_-2px_rgba(16,185,129,0.2)]'
                : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50',
            )}
          >
            <span
              className={cn(
                'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2',
                selected ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white',
              )}
            >
              {selected && (
                <svg
                  className="h-2.5 w-2.5 text-white"
                  viewBox="0 0 12 10"
                  fill="none"
                >
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            {Icon && (
              <Icon
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 shrink-0',
                  selected ? 'text-brand-700' : 'text-stone-500',
                )}
                strokeWidth={1.8}
              />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'text-[12.5px] font-semibold leading-tight',
                  selected ? 'text-brand-900' : 'text-ink',
                )}
              >
                {opt.label}
              </p>
              {opt.sublabel && (
                <p className="mt-0.5 truncate text-[10.5px] text-stone-500">
                  {opt.sublabel}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TravailCard({
  item,
  selected,
  recommended,
  onToggle,
}: {
  item: TravauxAgriOption;
  selected: boolean;
  recommended: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={() => onToggle(item.id)}
      className={cn(
        'group relative flex items-start gap-3 overflow-hidden rounded-xl border-2 p-3.5 text-left transition-all duration-150',
        selected
          ? 'border-brand-500 bg-brand-50/80 shadow-[0_4px_20px_-8px_rgba(16,185,129,0.25)]'
          : recommended
            ? 'border-accent-300 bg-accent-50/30 hover:border-accent-400'
            : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/40',
      )}
    >
      {recommended && !selected && (
        <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-md bg-accent-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-900">
          <Sparkles className="h-2.5 w-2.5" strokeWidth={2.4} />
          Reco
        </span>
      )}

      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2',
          selected
            ? 'border-brand-500 bg-brand-500'
            : 'border-stone-300 bg-white group-hover:border-stone-400',
        )}
      >
        <AnimatePresence>
          {selected && (
            <motion.span
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ duration: 0.15, ease: easeStripe }}
            >
              <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-[13.5px] font-semibold leading-snug',
            selected ? 'text-brand-900' : 'text-ink',
          )}
        >
          {item.label}
        </p>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-500">
          {item.sublabel}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {item.ceeCodes.map((code) => (
            <span
              key={code}
              className={cn(
                'inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[9.5px] font-bold tracking-wider ring-1 ring-inset',
                selected
                  ? 'bg-accent-100 text-accent-900 ring-accent-200/60'
                  : 'bg-stone-100 text-stone-600 ring-stone-200/70',
              )}
            >
              {code}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}
